import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { OccurrenceCount } from '../types';

export enum ApprovalStatus {
    APPROVED = 'Approved',
    AI_APPROVED = 'AI Approved',
    REFERRED = 'Referred',
    REJECTED = 'Rejected',
}

const statusConfigs = {
    [ApprovalStatus.REJECTED]: {
        description: 'Asset has been rejected due to rule violations',
        textColor: '#D52214', // Base red text
    },
    [ApprovalStatus.REFERRED]: {
        description: 'Requires additional review from moderator',
        textColor: '#E0B80E', // Base orange text
    },
    [ApprovalStatus.AI_APPROVED]: {
        description: 'Asset has been approved by the AI System',
        textColor: '#1976D2', // Base blue text
    },
    [ApprovalStatus.APPROVED]: {
        description: 'Asset has been approved for use',
        textColor: '#389E0D', // Base green text
    },
};

interface ApprovalStatusChartProps {
    data: OccurrenceCount[];
}

const ApprovalStatusChart: React.FC<ApprovalStatusChartProps> = ({ data }) => {
    // Ensure all approval statuses are represented in the data
    const completeData = Object.values(ApprovalStatus).map((status) => {
        const existingItem = data.find((item) => item.name === status);
        if (existingItem) {
            return existingItem;
        }
        // Create item with 0 occurrences if not found
        return {
            name: status,
            label: status,
            occurrence: 0,
        };
    });

    // Sort data according to enum order
    const orderedData = completeData.sort((a, b) => {
        const statusOrder = Object.values(ApprovalStatus);
        return (
            statusOrder.indexOf(a.name as ApprovalStatus) -
            statusOrder.indexOf(b.name as ApprovalStatus)
        );
    });

    // Transform the data to the format Nivo expects
    const chartData = orderedData.map((item) => ({
        id: item.name,
        label: item.label,
        value: item.occurrence,
        color: statusConfigs[item.name as ApprovalStatus]?.textColor,
    }));

    return (
        <div className="chart">
            <ResponsiveBar
                data={chartData}
                indexBy="id"
                keys={['value']}
                margin={{ top: 20, right: 10, bottom: 30, left: 30 }}
                padding={0.3}
                valueScale={{ type: 'linear' }}
                indexScale={{ type: 'band', round: true }}
                colors={(bar) => bar.data.color || '#000000'}
                borderColor={{
                    from: 'color',
                    modifiers: [['darker', 1.6]],
                }}
                axisTop={null}
                axisRight={null}
                borderRadius={10}
                axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legendPosition: 'middle',
                    legendOffset: 32,
                    renderTick: (tick) => {
                        const item = orderedData.find(
                            (d) => d.name === tick.value,
                        );
                        return (
                            <g transform={`translate(${tick.x},${tick.y})`}>
                                <line stroke="#000" strokeWidth={1} y2={6} />
                                <text
                                    textAnchor="middle"
                                    dominantBaseline="text-before-edge"
                                    transform="translate(0,10)"
                                    style={{
                                        fontSize: 11,
                                        fill: '#333',
                                        fontFamily: 'Lato bold',
                                    }}
                                >
                                    {item ? item.label : tick.value}
                                </text>
                            </g>
                        );
                    },
                }}
                axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legendPosition: 'middle',
                    legendOffset: -40,
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor={{
                    from: 'color',
                    modifiers: [['brighter', 4]],
                }}
                legends={[]}
                role="application"
                ariaLabel="Approval status bar chart"
                barAriaLabel={(e) => `${e.id}: ${e.value} occurrences`}
                theme={{
                    tooltip: {
                        container: {
                            background: '#333',
                            color: '#fff',
                            fontSize: 12,
                            borderRadius: 2,
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
                            padding: '5px 9px',
                        },
                    },
                }}
                tooltip={({ id, value, label, color }) => {
                    const item = data.find(
                        (d) => `value - ${d.name}` === label,
                    );
                    return (
                        <div
                            style={{
                                padding: 12,
                                color: '#fff',
                                background: '#333',
                                borderLeft: `4px solid ${color}`,
                            }}
                        >
                            <span style={{ display: 'block', marginBottom: 4 }}>
                                <strong>{item ? item.label : id}</strong>
                            </span>
                            <span style={{ display: 'block' }}>
                                Occurrences: <strong>{value}</strong>
                            </span>
                            {item &&
                                statusConfigs[item.name as ApprovalStatus] && (
                                    <span
                                        style={{
                                            display: 'block',
                                            fontSize: '0.9em',
                                            marginTop: 4,
                                            opacity: 0.9,
                                        }}
                                    >
                                        {
                                            statusConfigs[
                                                item.name as ApprovalStatus
                                            ].description
                                        }
                                    </span>
                                )}
                        </div>
                    );
                }}
            />
        </div>
    );
};

export default ApprovalStatusChart;
